import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { APP_CONFIG, Helpers } from '@core/common-legacy';
import type { CompanySize } from '@core/company-types';
import type { IdentityHeaders } from '@core/http-legacy';
import { IdentityHeader } from '@core/http-legacy';
import type { ThemeName } from '@core/theme-util';
import { TimeFormat } from '@core/time-legacy';

import { environment } from '../../environments/environment';
import type { LocalizationSettings } from '../account-setup.types';
import type { CompanyDataResponse, RecruitmentInvolvement } from '../company/company.types';
import type { UserDataResponse } from '../user/user.types';

type CompanyRequest = {
  emailConfirmation: {
    data: {
      companyLanguage: string;
      companySize: CompanySize;
      adminRole: string;
      familiarWithAts: boolean;
      recruitmentInvolvement: RecruitmentInvolvement;
      invitedEmails?: string[];
      timeFormat24: boolean;
      weekStartDay: LocalizationSettings['weekStartDay'];
      invitedEmailBodyHtml?: string;
      fillWithSampleData: boolean;
      adminPreferences: { theme: ThemeName };
    };
    token: string;
  };
};

export type UserRequest = {
  admin: {
    firstName: string;
    lastName: string;
    password?: string;
    email: string;
    phone: string;
    avatar?: File;
    timeFormat24: boolean;
    weekStartDay: LocalizationSettings['weekStartDay'];
    preferredLanguage: string;
    invitationToken: string;
    adminPreferences: { theme: ThemeName };
  };
};

interface TrackingData extends Record<any, any> {
  company: { createdAt: number; id: number; name: string };
  companyId: number;
  companyName: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  name: string;
  phone: string;
}

export type PatchCompanyResponse = {
  code: string;
  companyId: number;
  email: string;
  trackingData: TrackingData;
};

@Injectable()
export class AccountSetupApiService {
  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private appConfig: { isE2e: boolean },
  ) {}

  public patchCompany(data: CompanyRequest): Observable<PatchCompanyResponse> {
    return <Observable<PatchCompanyResponse>>this.http
      .patch(environment.apiCompanyUrl, Helpers.underscoreKeys(data), {
        headers: this.provideIdentityHeaders(),
      })
      .pipe(map(res => Helpers.camelizeKeys(res)));
  }

  public createUser(data: UserRequest): Observable<unknown> {
    return <Observable<unknown>>this.http.post(
      environment.apiUserUrl,
      Helpers.underscoreKeys(data),
      {
        headers: this.provideIdentityHeaders(),
      },
    );
  }

  public createSSOUser(data: Omit<UserRequest, 'password'>): Observable<unknown> {
    return <Observable<unknown>>this.http.post(
      environment.apiSSOUserUrl,
      Helpers.underscoreKeys(data),
      {
        headers: this.provideIdentityHeaders(),
      },
    );
  }

  public createADPUser(data: Omit<UserRequest, 'password'>): Observable<unknown> {
    return <Observable<unknown>>this.http.post(
      environment.apiADPUserUrl,
      Helpers.underscoreKeys(data),
      {
        headers: this.provideIdentityHeaders(),
      },
    );
  }

  public getUserData(token: string): Observable<UserDataResponse> {
    return <Observable<UserDataResponse>>this.http
      .get(`${environment.apiUserDataUrl}${token}`, {
        headers: this.provideIdentityHeaders(),
      })
      .pipe(map(data => Helpers.camelizeKeys(data)));
  }

  public getCompanyData(token: string): Observable<CompanyDataResponse> {
    return <Observable<CompanyDataResponse>>this.http
      .get(`${environment.apiCompanyDataUrl}${token}`, {
        headers: this.provideIdentityHeaders(),
      })
      .pipe(map(data => Helpers.camelizeKeys(data)));
  }

  public getDefaultLocalizationSettings(): Observable<LocalizationSettings> {
    return this.http
      .get(`${environment.apiLocalizationSettingsUrl}`, {
        headers: this.provideIdentityHeaders(),
      })
      .pipe(
        map(data => Helpers.camelizeKeys(data)),
        map(response => ({
          timeFormat: response.timeFormat24 ? TimeFormat.Hour24 : TimeFormat.Hour12,
          weekStartDay: response.weekStartDay,
        })),
      );
  }

  public provideIdentityHeaders(): IdentityHeaders {
    return {
      [IdentityHeader.Client]: this.appConfig.isE2e ? 'AccountSetup - e2e' : 'AccountSetup',
      [IdentityHeader.Env]: environment.environmentName,
    };
  }
}
